import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import "./Login.css";
import oxfordLogo from "../media/oxford-logo-long.svg";

interface LoginProps {
  handleLoginButtonClick: (portal: string) => void;
}

const AdminLogin: React.FC<LoginProps> = ({ handleLoginButtonClick }) => {
  const [email, setEmail] = useState("");
  const [portal] = useState("admin");
  const [errorMessage, setErrorMessage] = useState("");
  const { loginWithMsal } = useAuth();
  const location = useLocation();
  const successMessage = location.state?.successMessage;
  const failMessage = location.state?.failMessage;
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
  const API_URL = process.env.REACT_APP_API_URL || "";
  const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
  const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

  const apiBaseUrl =
    window.location.hostname === "localhost"
      ? API_URL
      : window.location.hostname === new URL(PROD_APP_URL).hostname
        ? PROD_API_URL
        : DEV_API_URL;

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleLogin = async () => {
    if (!email) {
      setErrorMessage("Please enter your email.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    try {
      setLoadingSpinner(true);
      const response = await fetch(`${apiBaseUrl}/api/auth/verify-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, portal }),
      });

      const data = await response.json();
      if (response.ok) {
        const [token, authError] = await loginWithMsal(email, portal);
        if (token) {
          localStorage.setItem("token", token);
          localStorage.setItem("portalType", portal);
          handleLoginButtonClick(portal);
          window.location.href = `/${portal}`;
        } else {
          console.error("Unable to find authToken. Error:", authError);
          setErrorMessage(authError);
        }
      } else {
        console.error(
          "The email verification response was not valid. Response message:",
          data.message,
        );
        setErrorMessage(data.message || "Email verification failed.");
      }
    } catch (error) {
      console.error("Error verifying email:", error);
      setErrorMessage("An error occurred. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div
      className="content-container-login"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="login-container">
        <div className="mobile-warning">
          For the best experience, we recommend using a computer to use this
          application.
        </div>
        <img className="oxford-logo-long" src={oxfordLogo} alt="Logo" />
        <h2>Admin Login </h2>

        <div className="email-field">
          <label>Email:</label>

          <input
            className="form-control form-control-md"
            type="email"
            value={email}
            placeholder="Enter your email"
            onChange={(e) => setEmail(e.target.value)}
          ></input>
        </div>

        <button
          onClick={handleLogin}
          type="button"
          className={`oxbtn ${email !== "" ? "btn-purple" : "btn-login"}`}
        >
          Login
        </button>
        {/* <div className="pop-up-warning">
          ⚠️ Application maintenance is in progress.  You can continue to use
          the application but may experience slower performance during this
          time.
        </div> */}
        {/* <div className="pop-up-error">
          🚫 We’re performing a scheduled update. Please try again later.
        </div> */}
        <div>
          {successMessage && (
            <div className=" alert alert-success fade show">
              {successMessage}
            </div>
          )}
          {failMessage && (
            <div className=" alert alert-danger fade show">{failMessage}</div>
          )}
        </div>
        <div className="text-container-login" style={{ textAlign: "center" }}>
          {" "}
          <a href="/login">User Login</a>
        </div>
      </div>

      {errorMessage && (
        <div className="alert-message alert alert-danger alert-dismissible fade show">
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default AdminLogin;
